import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Users, AlertTriangle, CheckCircle, Clock, TrendingUp, BarChart2, FileText, File, Link as LinkIcon, Upload, Download, Trash2, Edit2, Save, Plus, Image } from 'react-feather';
import { FeatureTag } from '../ui/feature-tag';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import api, { featureTagApi } from '../../services/api';
import { calculateFeatureAnalytics, calculateTaskAge, calculateDaysUntilDue } from './analyticsUtils';
import { useAuth } from '../../context/AuthContext';
import { isManager } from '../../utils/roleUtils';
import './FeatureTagDetailModal.css';

const FeatureTagDetailModal = ({ 
  tag, 
  isOpen, 
  onClose, 
  tasks = [], 
  users = [],
  onTagUpdate 
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tagDetails, setTagDetails] = useState(null);
  const [description, setDescription] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [tagName, setTagName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  const [links, setLinks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [userIsManager, setUserIsManager] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [newLink, setNewLink] = useState({ url: '', title: '' });
  const fileInputRef = React.useRef(null);

  // Check user role
  useEffect(() => {
    const checkRole = async () => {
      try {
        const response = await api.get('/api/auth/role');
        const userRole = response.data?.role;
        setUserIsManager(isManager(userRole));
      } catch (err) {
        console.error('Error checking role:', err);
      }
    };
    if (isOpen && tag) {
      checkRole();
      fetchTagDetails();
    }
  }, [isOpen, tag]);

  // Fetch tag details including description, artifacts, and links
  const fetchTagDetails = async () => {
    if (!tag?.id) return;
    try {
      setLoading(true);
      const tagData = await featureTagApi.get(tag.id);
      setDescription(tagData.description || '');
      setTagName(tagData.name || tag.name || '');
      setArtifacts(tagData.artifacts || []);
      setLinks(tagData.links || []);
    } catch (err) {
      console.error('Error fetching tag details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initialize tag name when tag changes
  useEffect(() => {
    if (tag?.name) {
      setTagName(tag.name);
    }
  }, [tag?.name]);

  // Calculate analytics from tasks using modularized functions
  const analytics = React.useMemo(() => {
    return calculateFeatureAnalytics(tag, tasks, users);
  }, [tag, tasks, users]);

  // Handle description save
  const handleSaveDescription = async () => {
    if (!userIsManager) return;
    try {
      setLoading(true);
      await featureTagApi.updateDescription(tag.id, description);
      setEditingDescription(false);
      if (onTagUpdate) {
        const updatedTag = await featureTagApi.get(tag.id);
        onTagUpdate(updatedTag);
      }
    } catch (err) {
      console.error('Error saving description:', err);
      alert('Failed to save description');
    } finally {
      setLoading(false);
    }
  };

  // Handle name save
  const handleSaveName = async () => {
    if (!userIsManager) return;
    if (!tagName.trim()) {
      alert('Feature tag name cannot be empty');
      return;
    }
    try {
      setLoading(true);
      await featureTagApi.update(tag.id, { name: tagName.trim() });
      setEditingName(false);
      if (onTagUpdate) {
        const updatedTag = await featureTagApi.get(tag.id);
        onTagUpdate(updatedTag);
      }
    } catch (err) {
      console.error('Error saving name:', err);
      const errorMessage = err.response?.data?.error || 'Failed to save feature tag name';
      alert(errorMessage);
      // Reset to original name on error
      setTagName(tag.name);
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (!userIsManager) {
      alert('Only managers can upload artifacts');
      return;
    }
    try {
      setUploading(true);
      const uploadedArtifacts = await featureTagApi.uploadArtifacts(tag.id, files);
      setArtifacts([...uploadedArtifacts, ...artifacts]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading artifacts:', err);
      alert('Failed to upload artifacts');
    } finally {
      setUploading(false);
    }
  };

  // Handle artifact delete
  const handleDeleteArtifact = async (artifactId) => {
    if (!userIsManager) return;
    if (!window.confirm('Are you sure you want to delete this artifact?')) return;
    try {
      await featureTagApi.deleteArtifact(tag.id, artifactId);
      setArtifacts(artifacts.filter(a => a.id !== artifactId));
    } catch (err) {
      console.error('Error deleting artifact:', err);
      alert('Failed to delete artifact');
    }
  };

  // Handle link create
  const handleCreateLink = async () => {
    if (!userIsManager || !newLink.url.trim()) return;
    try {
      const link = await featureTagApi.createLink(tag.id, {
        url: newLink.url.trim(),
        title: newLink.title.trim() || null
      });
      setLinks([link, ...links]);
      setNewLink({ url: '', title: '' });
    } catch (err) {
      console.error('Error creating link:', err);
      alert('Failed to create link');
    }
  };

  // Handle link update
  const handleUpdateLink = async (linkId, linkData) => {
    if (!userIsManager) return;
    try {
      const updatedLink = await featureTagApi.updateLink(tag.id, linkId, linkData);
      setLinks(links.map(l => l.id === linkId ? updatedLink : l));
      setEditingLink(null);
    } catch (err) {
      console.error('Error updating link:', err);
      alert('Failed to update link');
    }
  };

  // Handle link delete
  const handleDeleteLink = async (linkId) => {
    if (!userIsManager) return;
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    try {
      await featureTagApi.deleteLink(tag.id, linkId);
      setLinks(links.filter(l => l.id !== linkId));
    } catch (err) {
      console.error('Error deleting link:', err);
      alert('Failed to delete link');
    }
  };

  // Get file icon
  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) {
      return <Image size={18} />;
    } else if (fileType === 'application/pdf') {
      return <FileText size={18} />;
    } else {
      return <File size={18} />;
    }
  };

  // Get hours indicator class based on hours spent
  const getHoursIndicator = (hours) => {
    const hoursNum = parseFloat(hours) || 0;
    if (hoursNum === 0) return 'task-hours--none';
    if (hoursNum < 2) return 'task-hours--low';
    if (hoursNum < 8) return 'task-hours--medium';
    return 'task-hours--high';
  };

  // Format due date for display
  const formatDueDate = (dueDate) => {
    if (!dueDate) return 'No due date set';
    const date = new Date(dueDate);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format created date for display (short)
  const formatCreatedDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Humanize task type for display
  const formatTaskType = (taskType) => {
    if (!taskType) return '—';
    return String(taskType).replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Get risk level styling
  const getRiskStyling = (riskLevel) => {
    switch (riskLevel) {
      case 'critical':
        return { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' };
      case 'high':
        return { color: '#d97706', bg: '#fef3c7', border: '#fed7aa' };
      case 'medium':
        return { color: '#d97706', bg: '#fef3c7', border: '#fed7aa' };
      default:
        return { color: '#059669', bg: '#f0fdf4', border: '#bbf7d0' };
    }
  };

  if (!isOpen || !tag) return null;

  return createPortal(
    <div className="feature-tag-detail-modal-overlay" onClick={onClose}>
      <div className="feature-tag-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="feature-tag-detail-modal__header">
          <div className="feature-tag-detail-modal__title">
            {editingName ? (
              <div className="feature-tag-detail-modal__name-edit">
                <TextField>
                  <TextField.Input
                    value={tagName}
                    onChange={(e) => setTagName(e.target.value)}
                    placeholder="Feature tag name"
                    maxLength={50}
                    autoFocus
                  />
                </TextField>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveName}
                  disabled={loading || !tagName.trim()}
                >
                  <Save size={14} />
                  Save
                </Button>
                <Button
                  variant="neutral-tertiary"
                  size="sm"
                  onClick={() => {
                    setEditingName(false);
                    setTagName(tag.name);
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="feature-tag-detail-modal__title-content">
            <FeatureTag 
                  name={tagName || tag.name} 
              color={tag.color}
              size="medium"
            />
                {userIsManager && (
                  <Button
                    variant="neutral-tertiary"
                    size="sm"
                    onClick={() => setEditingName(true)}
                    className="feature-tag-detail-modal__edit-name-btn"
                  >
                    <Edit2 size={14} />
                  </Button>
                )}
              </div>
            )}
            <h2>Feature Details</h2>
          </div>
          <button 
            className="feature-tag-detail-modal__close"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="feature-tag-detail-modal__content">
          <div className="feature-tag-detail-modal__content-row">
          <div className="feature-tag-detail-modal__main-column">
          {/* Overview Section */}
          <div className="feature-tag-detail-modal__section">
            <h3>Overview</h3>
            <div className="feature-tag-detail-modal__overview">
              <div className="feature-tag-detail-modal__overview-item">
                <Calendar size={16} />
                <span className="feature-tag-detail-modal__overview-label">Due Date:</span>
                <span className="feature-tag-detail-modal__overview-value">{formatDueDate(tag.due_date)}</span>
              </div>
              {analytics && analytics.daysRemaining !== null && (
                <div className="feature-tag-detail-modal__overview-item">
                  <Clock size={16} />
                  <span className="feature-tag-detail-modal__overview-label">Days Remaining:</span>
                  <span className={`feature-tag-detail-modal__overview-value ${analytics.daysRemaining < 0 ? 'overdue' : ''}`}>
                    {analytics.daysRemaining < 0 ? `${Math.abs(analytics.daysRemaining)} days overdue` : `${analytics.daysRemaining} days`}
                  </span>
                </div>
              )}
            </div>
          </div>

            {/* Description Section */}
            <div className="feature-tag-detail-modal__section">
              <div className="feature-tag-detail-modal__section-header">
                <h3>Description</h3>
                {userIsManager && !editingDescription && (
                  <Button
                    variant="neutral-tertiary"
                    size="sm"
                    onClick={() => setEditingDescription(true)}
                  >
                    <Edit2 size={14} />
                    Edit
                  </Button>
                )}
              </div>
              {editingDescription ? (
                <div className="feature-tag-detail-modal__description-edit">
                  <TextArea>
                    <TextArea.Input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Add a description for this feature tag..."
                      rows={4}
                      maxLength={5000}
                    />
                  </TextArea>
                  <div className="feature-tag-detail-modal__description-actions">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSaveDescription}
                      disabled={loading}
                    >
                      <Save size={14} />
                      Save
                    </Button>
                    <Button
                      variant="neutral-tertiary"
                      size="sm"
                      onClick={() => {
                        setEditingDescription(false);
                        fetchTagDetails();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="feature-tag-detail-modal__description">
                  {description ? (
                    <p className="feature-tag-detail-modal__description-text">{description}</p>
                  ) : (
                    <p className="feature-tag-detail-modal__description-empty">
                      {userIsManager ? 'No description. Click Edit to add one.' : 'No description available.'}
                    </p>
                  )}
                </div>
              )}
            </div>

          {/* Progress Section */}
          {analytics && (
            <div className="feature-tag-detail-modal__section">
              <h3>Progress</h3>
              <div className="feature-tag-detail-modal__progress">
                <div className="feature-tag-detail-modal__progress-header">
                  <span className="feature-tag-detail-modal__progress-percentage">{analytics.completionPercentage}%</span>
                  <span className="feature-tag-detail-modal__progress-text">
                    {analytics.completedTasks} of {analytics.totalTasks} tasks completed
                  </span>
                </div>
                <div className="feature-tag-detail-modal__progress-bar">
                  <div 
                    className="feature-tag-detail-modal__progress-bar__fill"
                    style={{ width: `${analytics.completionPercentage}%` }}
                  />
                </div>
                <div className="feature-tag-detail-modal__progress-stats">
                  <div className="feature-tag-detail-modal__progress-stat">
                    <CheckCircle size={14} />
                    <span>{analytics.completedTasks} Completed</span>
                  </div>
                  <div className="feature-tag-detail-modal__progress-stat">
                    <Clock size={14} />
                    <span>{analytics.inProgressTasks} In Progress</span>
                  </div>
                  <div className="feature-tag-detail-modal__progress-stat">
                    <AlertTriangle size={14} />
                    <span>{analytics.openTasks} Open</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Hours Summary */}
          {userIsManager && analytics && (
            <div className="feature-tag-detail-modal__section">
              <h3>Hours Summary</h3>
              <p className="feature-tag-detail-modal__section-description">
                Time tracking data for this feature. Total hours spent across all tasks and average hours per task.
              </p>
              <div className="feature-tag-detail-modal__hours">
                <div className="feature-tag-detail-modal__hours-grid">
                  <div className="feature-tag-detail-modal__hours-stat">
                    <div className="feature-tag-detail-modal__hours-stat__value">{analytics.totalHours}h</div>
                    <div className="feature-tag-detail-modal__hours-stat__label">Total Hours</div>
                  </div>
                  <div className="feature-tag-detail-modal__hours-stat">
                    <div className="feature-tag-detail-modal__hours-stat__value">{analytics.averageHoursPerTask}h</div>
                    <div className="feature-tag-detail-modal__hours-stat__label">Avg per Task</div>
                  </div>
                  <div className="feature-tag-detail-modal__hours-stat">
                    <div className="feature-tag-detail-modal__hours-stat__value">{analytics.completedHours}h</div>
                    <div className="feature-tag-detail-modal__hours-stat__label">Completed Hours</div>
                  </div>
                  <div className="feature-tag-detail-modal__hours-stat">
                    <div className="feature-tag-detail-modal__hours-stat__value">{analytics.totalHours - analytics.completedHours}h</div>
                    <div className="feature-tag-detail-modal__hours-stat__label">Remaining Hours</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Risk Assessment */}
          {userIsManager && analytics && (
            <div className="feature-tag-detail-modal__section">
              <h3>Risk Assessment</h3>
              <div 
                className="feature-tag-detail-modal__risk"
                style={{
                  backgroundColor: getRiskStyling(analytics.riskLevel).bg,
                  borderColor: getRiskStyling(analytics.riskLevel).border,
                  color: getRiskStyling(analytics.riskLevel).color
                }}
              >
                <AlertTriangle size={16} />
                <span className="risk-level">{analytics.riskLevel.toUpperCase()}</span>
                <span className="risk-message">{analytics.riskMessage}</span>
              </div>
            </div>
          )}

          {/* Feature Blocker Status */}
          {userIsManager && analytics && (
            <div className="feature-tag-detail-modal__section">
              <h3>Feature Blocker Status</h3>
              <div className={`feature-tag-detail-modal__blocker-status ${analytics.blockers.length > 0 ? 'blocker-true' : 'blocker-false'}`}>
                <span className="blocker-status-indicator">
                  {analytics.blockers.length > 0 ? '⚠️' : '✅'}
                </span>
                <span className="blocker-status-text">
                  {analytics.blockers.length > 0 ? 'Feature is BLOCKED' : 'Feature is on track'}
                </span>
                {analytics.blockers.length > 0 && (
                  <span className="blocker-status-details">
                    ({analytics.blockers.length} stagnant task{analytics.blockers.length !== 1 ? 's' : ''})
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Blocker Analysis */}
          {userIsManager && analytics && analytics.blockers.length > 0 && (
            <div className="feature-tag-detail-modal__section">
              <h3>Stagnant Tasks</h3>
              {tag.due_date && (() => {
                const dueDate = new Date(tag.due_date);
                const daysUntilDue = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));
                const isNearDue = daysUntilDue <= 3;
                
                if (isNearDue) {
                  return (
                    <div className="feature-tag-detail-modal__urgent-notice">
                      <AlertTriangle size={16} />
                      <span>
                        {daysUntilDue < 0 
                          ? `Feature is ${Math.abs(daysUntilDue)} day${Math.abs(daysUntilDue) !== 1 ? 's' : ''} overdue!`
                          : `Feature is due in ${daysUntilDue} day${daysUntilDue !== 1 ? 's' : ''}!`
                        }
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
              <p className="feature-tag-detail-modal__section-description">
                We help you spot tasks that might be holding up your feature. If you're running out of time (3 days or less), we flag everything that's not done. Otherwise, we look for tasks that seem stuck - either they've been open for a week with no work logged, or they've been in progress for 10 days. We're smart enough to ignore tasks you just created.
              </p>
              <div className="feature-tag-detail-modal__blockers">
                {analytics.blockers.map(task => (
                  <div key={task.id} className="blocker-item">
                    <div className="blocker-info">
                      <span className="blocker-title">{task.title}</span>
                      <span className="blocker-status">{task.status}</span>
                    </div>
                    <div className="blocker-meta">
                      {task.display_id && (
                        <span className="blocker-meta-item">
                          <strong>ID:</strong> {task.display_id}
                        </span>
                      )}
                      <span className="blocker-meta-item">
                        <strong>Type:</strong> {formatTaskType(task.task_type)}
                      </span>
                      <span className="blocker-meta-item">
                        <strong>Hours:</strong> {parseFloat(task.hours) || 0}h
                      </span>
                      <span className="blocker-meta-item">
                        <strong>Created:</strong> {formatCreatedDate(task.created_at)}
                      </span>
                    </div>
                    <div className="blocker-assignee">
                      {task.assigned_to ? (
                        <span>
                          Assigned to: {task.assigned_to.display_name || users.find(u => u.id === (task.assigned_to.id || task.assigned_to))?.display_name || 'Unknown'}
                        </span>
                      ) : (
                        <span className="feature-tag-detail-modal__unassigned">Unassigned</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Workload Distribution */}
          {userIsManager && analytics && analytics.assigneeWorkload.length > 0 && (
            <div className="feature-tag-detail-modal__section">
              <h3>Team Workload</h3>
              <p className="feature-tag-detail-modal__section-description">
                Task distribution across team members for this feature. Helps identify workload balance and potential bottlenecks.
              </p>
              <div className="feature-tag-detail-modal__workload">
                {analytics.assigneeWorkload.map(workload => (
                  <div key={workload.user.id} className="workload-item">
                    <div className="workload-header">
                      <Users size={14} />
                      <span className="workload-name">{workload.user.display_name}</span>
                      <span className="workload-total">{workload.total} tasks</span>
                    </div>
                    <div className="workload-breakdown">
                      <div className="workload-stat completed">
                        <CheckCircle size={12} />
                        <span>{workload.completed}</span>
                      </div>
                      <div className="workload-stat inprogress">
                        <Clock size={12} />
                        <span>{workload.inprogress}</span>
                      </div>
                      <div className="workload-stat open">
                        <AlertTriangle size={12} />
                        <span>{workload.open}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          </div>

          {/* Right Sidebar - Artifacts and Links */}
          <div className="feature-tag-detail-modal__sidebar">
            {/* Artifacts Section */}
            <div className="feature-tag-detail-modal__section">
              <div className="feature-tag-detail-modal__section-header">
                <h3>Artifacts ({artifacts.length})</h3>
                {userIsManager && (
                  <div className="feature-tag-detail-modal__upload-wrapper">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="neutral-tertiary"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      <Upload size={14} />
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                )}
              </div>
              {artifacts.length > 0 ? (
                <div className="feature-tag-detail-modal__artifacts-list">
                  {artifacts.map(artifact => (
                    <div key={artifact.id} className="feature-tag-detail-modal__artifact-item">
                      <div className="feature-tag-detail-modal__artifact-info">
                        {getFileIcon(artifact.file_type)}
                        <div>
                          <div className="feature-tag-detail-modal__artifact-name">{artifact.file_name}</div>
                          <div className="feature-tag-detail-modal__artifact-meta">
                            {(artifact.file_size / 1024).toFixed(1)} KB • {new Date(artifact.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="feature-tag-detail-modal__artifact-actions">
                        <a
                          href={artifact.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="feature-tag-detail-modal__artifact-download"
                        >
                          <Download size={16} />
                        </a>
                        {userIsManager && (
                          <button
                            className="feature-tag-detail-modal__artifact-delete"
                            onClick={() => handleDeleteArtifact(artifact.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="feature-tag-detail-modal__empty-state">
                  {userIsManager ? 'No artifacts. Click Upload to add files.' : 'No artifacts available.'}
                </p>
              )}
            </div>

            {/* Links Section */}
            <div className="feature-tag-detail-modal__section">
              <div className="feature-tag-detail-modal__section-header">
                <h3>Links ({links.length})</h3>
              </div>
              {userIsManager && (
                <div className="feature-tag-detail-modal__link-form">
                  <TextField>
                    <TextField.Input
                      value={newLink.url}
                      onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                      placeholder="URL (required)"
                      type="url"
                    />
                  </TextField>
                  <TextField>
                    <TextField.Input
                      value={newLink.title}
                      onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                      placeholder="Title (optional)"
                      maxLength={200}
                    />
                  </TextField>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleCreateLink}
                    disabled={!newLink.url.trim()}
                  >
                    <Plus size={14} />
                    Add Link
                  </Button>
                </div>
              )}
              {links.length > 0 ? (
                <div className="feature-tag-detail-modal__links-list">
                  {links.map(link => (
                    <div key={link.id} className="feature-tag-detail-modal__link-item">
                      {editingLink === link.id ? (
                        <div className="feature-tag-detail-modal__link-edit">
                          <TextField>
                            <TextField.Input
                              value={editingLink.url}
                              onChange={(e) => setEditingLink({ ...editingLink, url: e.target.value })}
                              placeholder="URL"
                              type="url"
                            />
                          </TextField>
                          <TextField>
                            <TextField.Input
                              value={editingLink.title || ''}
                              onChange={(e) => setEditingLink({ ...editingLink, title: e.target.value })}
                              placeholder="Title (optional)"
                              maxLength={200}
                            />
                          </TextField>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleUpdateLink(link.id, {
                              url: editingLink.url,
                              title: editingLink.title || null
                            })}
                          >
                            <Save size={14} />
                            Save
                          </Button>
                          <Button
                            variant="neutral-tertiary"
                            size="sm"
                            onClick={() => setEditingLink(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="feature-tag-detail-modal__link-info">
                            <LinkIcon size={16} />
                            <div>
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="feature-tag-detail-modal__link-url"
                              >
                                {link.title || link.url}
                              </a>
                              {link.title && (
                                <div className="feature-tag-detail-modal__link-url-sub">{link.url}</div>
                              )}
                            </div>
                          </div>
                          {userIsManager && (
                            <div className="feature-tag-detail-modal__link-actions">
                              <button
                                className="feature-tag-detail-modal__link-edit-btn"
                                onClick={() => setEditingLink({ ...link })}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                className="feature-tag-detail-modal__link-delete"
                                onClick={() => handleDeleteLink(link.id)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="feature-tag-detail-modal__empty-state">
                  {userIsManager ? 'No links. Add a link above.' : 'No links available.'}
                </p>
              )}
            </div>
          </div>
          </div>

          {/* All Tasks - full width */}
          <div className="feature-tag-detail-modal__section feature-tag-detail-modal__section--full-width">
            <h3>All Tasks ({analytics?.totalTasks || 0})</h3>
            <p className="section-description">
              Complete task list with ages and hours. <strong>Red-highlighted tasks are potential blockers:</strong> if your feature is due soon (<strong>3 days or less</strong>), we flag any incomplete task. Otherwise, we flag stuck tasks (<strong>open for 7+ days with no work logged, or in-progress for 10+ days</strong>) only when they make up <strong>20% or more of your total tasks</strong>. We're smart enough to ignore recently created tasks.
            </p>
            <div className="feature-tag-detail-modal__tasks">
              <table className="feature-tag-detail-modal__tasks-table">
                <thead>
                  <tr>
                    <th>Task ID</th>
                    <th>Task</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Age</th>
                    <th>Hours</th>
                    <th>Created On</th>
                    <th>Assignee</th>
                  </tr>
                </thead>
                <tbody>
              {tasks
                .filter(task => task.feature_tag?.id === tag.id)
                .map(task => {
                  const taskAge = calculateTaskAge(task.created_at);
                  const hoursSpent = parseFloat(task.hours) || 0;
                  const daysUntilDue = calculateDaysUntilDue(tag.due_date);
                  let shouldBeBlocker = false;
                  if (daysUntilDue !== null && daysUntilDue <= 3) {
                    shouldBeBlocker = task.status !== 'completed';
                  } else {
                    const isStagnantOpen = task.status === 'open' && hoursSpent === 0 && taskAge >= 7;
                    const isStagnantInProgress = task.status === 'in-progress' && taskAge >= 10;
                    const isRecentTask = taskAge <= 2;
                    const isRecentInProgressWithHours = task.status === 'in-progress' && hoursSpent >= 5;
                    if (isRecentTask && !isRecentInProgressWithHours) {
                      shouldBeBlocker = false;
                    } else {
                      shouldBeBlocker = isStagnantOpen || isStagnantInProgress;
                    }
                  }
                  return (
                        <tr key={task.id} className={`task-row ${shouldBeBlocker ? 'task-row--blocker' : ''}`}>
                          <td className="task-cell task-cell--id">{task.display_id || '—'}</td>
                          <td className="task-cell task-cell--title">
                            {shouldBeBlocker && <span className="task-blocker-icon">⚠️</span>}
                            <span className="task-title">{task.title}</span>
                          </td>
                          <td className="task-cell task-cell--type">{formatTaskType(task.task_type)}</td>
                          <td className="task-cell">
                            <span className={`task-status task-status--${task.status}`}>{task.status}</span>
                          </td>
                          <td className="task-cell">
                            <span className="task-age">{taskAge} day{taskAge !== 1 ? 's' : ''}</span>
                          </td>
                          <td className="task-cell">
                            <span className={`task-hours ${getHoursIndicator(task.hours)}`}>{parseFloat(task.hours) || 0}h</span>
                          </td>
                          <td className="task-cell task-cell--created">{formatCreatedDate(task.created_at)}</td>
                          <td className="task-cell">
                            {task.assigned_to ? (
                              <span className="task-assignee">
                                {task.assigned_to.display_name || users.find(u => u.id === (task.assigned_to.id || task.assigned_to))?.display_name || 'Unknown'}
                              </span>
                            ) : (
                              <span className="feature-tag-detail-modal__unassigned">Unassigned</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="feature-tag-detail-modal__footer">
          <Button
            variant="neutral-tertiary"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FeatureTagDetailModal;
